import { INFO_NOT_AVAILABLE_NA } from '../const/infoNotAvailableText'

type FormatFullNameArgs = {
  givenName?: string | null
  familyName?: string | null
  /** Renders the family name as an initial, e.g. `Ada L.`. */
  abbreviateFamilyName?: boolean
  fallback?: string
  /**
   * Caps the formatted name at this many characters, ellipsis included.
   * Off by default, set it where the layout cannot take an arbitrary name.
   */
  maxLength?: number | false
}

const truncate = (name: string, maxLength: number): string =>
  name.length > maxLength ? `${name.slice(0, maxLength - 1).trimEnd()}…` : name

/**
 * Formats a person's name for display, tolerating missing parts.
 *
 * Name fields are typically nullable and a subgraph may withhold them rather
 * than failing, so interpolating the parts directly renders "null null".
 * Joining whatever is present also avoids "Ada -" when only one part exists.
 * Returns the fallback (`N/A` by default) when nothing is left to show.
 */
export const formatFullName = ({
  givenName,
  familyName,
  abbreviateFamilyName = false,
  fallback = INFO_NOT_AVAILABLE_NA,
  maxLength = false,
}: FormatFullNameArgs): string => {
  const given = givenName?.trim() ?? ''
  const family = familyName?.trim() ?? ''
  const shouldAbbreviate =
    abbreviateFamilyName && given.length > 0 && family.length > 0
  const parts = [given, shouldAbbreviate ? `${family.charAt(0)}.` : family]
  const name = parts.filter(part => part.length > 0).join(' ')

  if (name.length === 0) {
    return fallback
  }

  return maxLength === false ? name : truncate(name, maxLength)
}
